import { useState, type CSSProperties } from 'react';
import type { NightRun, Offer } from '../../../campaign';
import { t } from '../../../i18n';
import { CardView } from '../CardView';
import { blocked } from '../NightCounter';
import { affords } from '../NightParts';
import { Emblem } from './Emblem';
import { ItemSheet, showOffer } from './Item';
import { NightObject } from './Objects';

/**
 * Things laid out to be chosen (docs/LONG_NIGHT_ART_DIRECTION.md §11.9, §11.14–§11.15): upright
 * on velvet under the lamp, with a paper ticket on string when they have a price, their name
 * beneath when they don't. A tap reads one; its sheet takes it. The cloth changes with the
 * scene: velvet at the Spoils, wrapping cloth at the Fence, a dusty box in Lost and Found,
 * smoke at the Séance.
 */

/** A good, as the object it is (a deck card as a card). */
export function Good({ offer, size }: { offer: Offer; size: number }) {
  if (offer.kind === 'card')
    return (
      <CardView
        size={size >= 70 ? 'md' : 'sm'}
        face={offer.card.kind === 'normal' ? offer.card.face : null}
        wild={offer.card.kind === 'wild'}
        stamp={offer.card.stamp}
        stampValue={offer.card.value}
        finish={offer.card.finish}
        trick={offer.card.trick}
        iou={offer.card.iou}
        label=""
      />
    );
  const o = showOffer(offer).object;
  return o ? <NightObject spec={o} size={size} /> : <Emblem name="unknown" size={size} />;
}

export type Cloth = 'velvet' | 'wrapped' | 'box' | 'smoke' | 'bench';

export function VelvetTray({
  run,
  offers,
  actionLabel,
  onTake,
  cloth = 'velvet',
  label,
}: {
  run: NightRun;
  offers: readonly { offer: Offer; price?: number; sold?: boolean }[];
  actionLabel: (price: number | undefined) => string;
  onTake: (index: number) => void;
  cloth?: Cloth;
  label?: string;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const size = offers.length >= 5 ? 56 : offers.length === 4 ? 64 : 72;
  const o = open !== null ? offers[open] : undefined;
  const why = o ? blocked(run, o.offer, o.price ?? 0) : null;
  return (
    <>
      <div className={`ct-tray vt vt-${cloth}`} aria-label={label}>
        <div className="ct-goods" style={{ '--good': `${size}px` } as CSSProperties}>
          {offers.map((x, i) => (
            <button
              key={`${i}-${JSON.stringify(x.offer)}`}
              type="button"
              className={`ct-good ${x.sold ? 'is-sold' : ''}`}
              style={{ '--d': i } as CSSProperties}
              disabled={x.sold}
              onClick={() => setOpen(i)}
              aria-label={`${showOffer(x.offer).name}${x.price ? ` · ¤${x.price}` : ''}`}
            >
              {!x.sold && (
                <>
                  <span className="ct-object">
                    <Good offer={x.offer} size={size} />
                  </span>
                  {x.price !== undefined ? (
                    <>
                      <span className="ct-string" aria-hidden="true" />
                      <span className={`ct-ticket ${affords(run, x.price) ? '' : 'is-short'}`}>
                        {x.price === 0 ? t('night.free') : `¤${x.price}`}
                      </span>
                    </>
                  ) : (
                    <span className="vt-name">{showOffer(x.offer).name}</span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      </div>
      {o && open !== null && (
        <ItemSheet
          item={showOffer(o.offer)}
          price={o.price}
          note={why}
          actions={[
            {
              label: actionLabel(o.price),
              primary: true,
              disabled: why !== null,
              onClick: () => {
                setOpen(null);
                onTake(open);
              },
            },
          ]}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
